import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { UserCog, Users, Bike, Car, TrendingUp, Plus, Activity, Utensils } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

export const OverviewPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/superadmin/stats`);
        setStats(res.data);
      } catch (err) {
        console.error("Failed to fetch stats:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const statCards = [
    { label: "Total Admins", value: stats?.total_admins || 0, icon: UserCog, color: "bg-purple-500", subtext: `${stats?.active_admins || 0} active` },
    { label: "Total Drivers", value: stats?.total_drivers || 0, icon: Bike, color: "bg-blue-500", subtext: "Registered drivers" },
    { label: "Total Customers", value: stats?.total_customers || 0, icon: Users, color: "bg-green-500", subtext: "Registered customers" },
    { label: "Total Rides", value: stats?.total_rides || 0, icon: Car, color: "bg-orange-500", subtext: "All time" },
  ];

  // Today's stats (if available from API)
  const todayStatCards = stats?.today_bike_taxi !== undefined ? [
    { label: "Today Bike Taxi", value: stats?.today_bike_taxi || 0, icon: Bike, color: "bg-purple-500", subtext: "Today's rides" },
    { label: "Today Food Delivery", value: stats?.today_food_delivery || 0, icon: Utensils, color: "bg-orange-500", subtext: "Today's orders" },
  ] : [];

  return (
    <div data-testid="superadmin-overview">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground">Platform Overview</h2>
        <p className="text-muted-foreground">Monitor your platform's performance</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((stat, i) => (
          <div key={i} className="bg-card rounded-xl border border-border p-4 md:p-6">
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <div className={`w-10 h-10 md:w-12 md:h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                <stat.icon className="w-5 h-5 md:w-6 md:h-6 text-white" />
              </div>
              <TrendingUp className="w-4 h-4 md:w-5 md:h-5 text-green-500" />
            </div>
            <p className="text-2xl md:text-3xl font-bold text-foreground">{stat.value}</p>
            <p className="text-sm text-muted-foreground">{stat.label}</p>
            <p className="text-xs text-muted-foreground mt-1 hidden md:block">{stat.subtext}</p>
          </div>
        ))}
      </div>

      {/* Today's Stats - if available */}
      {todayStatCards.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {todayStatCards.map((stat, i) => (
            <div key={i} className="bg-card rounded-xl border border-border p-4 md:p-6">
              <div className="flex items-center justify-between mb-3 md:mb-4">
                <div className={`w-10 h-10 md:w-12 md:h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                  <stat.icon className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
                <TrendingUp className="w-4 h-4 md:w-5 md:h-5 text-green-500" />
              </div>
              <p className="text-2xl md:text-3xl font-bold text-foreground">{stat.value}</p>
              <p className="text-sm text-muted-foreground">{stat.label}</p>
              <p className="text-xs text-muted-foreground mt-1 hidden md:block">{stat.subtext}</p>
            </div>
          ))}
        </div>
      )}

      <div className="bg-card rounded-xl border border-border p-4 md:p-6">
        <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <Button variant="outline" className="h-auto py-3 md:py-4 flex flex-col items-center gap-2" onClick={() => navigate('/superadmin/admins')}>
            <UserCog className="w-5 h-5 md:w-6 md:h-6" />
            <span className="text-xs md:text-sm">Manage Admins</span>
          </Button>
          <Button variant="outline" className="h-auto py-3 md:py-4 flex flex-col items-center gap-2" onClick={() => navigate('/superadmin/drivers')}>
            <Bike className="w-5 h-5 md:w-6 md:h-6" />
            <span className="text-xs md:text-sm">All Drivers</span>
          </Button>
          <Button variant="outline" className="h-auto py-3 md:py-4 flex flex-col items-center gap-2" onClick={() => navigate('/superadmin/payments')}>
            <Activity className="w-5 h-5 md:w-6 md:h-6" />
            <span className="text-xs md:text-sm">Admin Payments</span>
          </Button>
          <Button variant="outline" className="h-auto py-3 md:py-4 flex flex-col items-center gap-2" onClick={() => navigate('/superadmin/config')}>
            <Car className="w-5 h-5 md:w-6 md:h-6" />
            <span className="text-xs md:text-sm">Settings</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OverviewPage;
